import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import UniversalSchemaEditor from './shared/UniversalSchemaEditor'
import PanelLayoutEditor from './PanelLayoutEditor'
import FloorPlanEditor from './FloorPlanEditor'
import NetworkSchemaEditor from './NetworkSchemaEditor'

// Get schema info to determine which editor to use
async function getSchemaInfo(schemaId: number) {
  const response = await fetch(`/api/schemas/${schemaId}`)
  if (!response.ok) throw new Error('Failed to load schema')
  const data = await response.json()
  return data.success ? data.data : null
}

export default function SchemaEditorRouter() {
  const { projectId, schemaId } = useParams<{ projectId: string; schemaId: string }>()

  const parsedSchemaId = schemaId ? parseInt(schemaId, 10) : null

  // Load schema to check its type
  const { data: schemaData, isLoading, error } = useQuery({
    queryKey: ['schema-info', parsedSchemaId],
    queryFn: () => getSchemaInfo(parsedSchemaId!),
    enabled: !!parsedSchemaId,
  })

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-lg text-gray-600">Загрузка схемы...</div>
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h3 className="text-lg font-medium text-red-700 mb-2">Ошибка</h3>
          <p className="text-red-500">Не удалось загрузить схему</p>
        </div>
      </div>
    )
  }

  // Check schema type and route to appropriate editor
  const schemaTypeCode = schemaData?.schema_type

  // If panel_layout type, use PanelLayoutEditor
  if (schemaTypeCode === 'panel_layout') {
    const electricalPanelId = schemaData?.electrical_panel_id

    if (!electricalPanelId) {
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <h3 className="text-lg font-medium text-red-700 mb-2">Ошибка</h3>
            <p className="text-red-500">Для схемы щита требуется указать электрощит</p>
          </div>
        </div>
      )
    }

    return (
      <PanelLayoutEditor
        projectId={projectId!}
        schemaId={schemaId!}
        electricalPanelId={electricalPanelId}
      />
    )
  }

  if (schemaTypeCode === 'floor_plan') {
    return (
      <FloorPlanEditor
        projectId={projectId!}
        schemaId={schemaId!}
        electricalPanelId={schemaData?.electrical_panel_id}
      />
    )
  }

  if (schemaTypeCode === 'network') {
    return (
      <NetworkSchemaEditor
        projectId={projectId!}
        schemaId={schemaId!}
      />
    )
  }

  // For all other types, use UniversalSchemaEditor
  return <UniversalSchemaEditor />
}

